import type {
  ContactFormValues,
  LocalizedCopy,
  NavItem,
  ProjectCategoryBlock,
  ServiceItem,
  SolutionCategory,
  StatItem,
} from '@/types/site'

const yearsInOperation = Math.max(new Date().getFullYear() - 2018, 1)

export const navItems: NavItem[] = [
  { key: 'home', slug: '', label: { en: 'Home', ar: 'الرئيسية' } },
  { key: 'about', slug: 'about', label: { en: 'About Us', ar: 'من نحن' } },
  { key: 'services', slug: 'services', label: { en: 'Services', ar: 'الخدمات' } },
  { key: 'maintenance', slug: 'maintenance-team', label: { en: 'Maintenance Team', ar: 'فريق الصيانة' } },
  { key: 'projects', slug: 'projects', label: { en: 'Projects', ar: 'المشاريع' } },
  { key: 'resources', slug: 'resources', label: { en: 'Resources', ar: 'الموارد' } },
  { key: 'blog', slug: 'blog', label: { en: 'Blog', ar: 'المقالات' } },
  { key: 'contact', slug: 'contact', label: { en: 'Contact', ar: 'تواصل معنا' } },
]

export const siteContent = {
  company: {
    name: 'Pro Damac General Trading',
    legalName: {
      en: 'Pro Damac General Trading Company',
      ar: 'شركة برو دماك للتجارة العامة',
    },
    descriptor: {
      en: 'General Trading for the General Contracting Sector',
      ar: 'التجارة العامة لقطاع المقاولات العامة',
    },
    description: {
      en: "Pro Damac is one of Kuwait's leading general trading companies, specialized in supplies, installations, and maintenance contracts for all equipment and materials of the general contracting sector. Established in Kuwait in 2018, we work through strategic partnerships and authorized distribution agreements with leading global brands to deliver high-quality products and integrated systems that meet the needs of both local and regional markets. Our role spans the full cycle: supplying premium materials and equipment, executing installations on site, and providing maintenance contracts that keep projects performing efficiently. With a team of highly skilled engineers, technicians, and administrators, we adopt the latest methods and advanced technologies to guarantee superior execution within optimal timeframes. We have established ourselves as a trusted partner for both public and private sectors, committed to long-term relationships built on trust, innovation, and excellence.",
      ar: 'تُعدّ شركة برو دماك إحدى شركات التجارة العامة المتقدمة في الكويت، متخصصة في التوريدات والتركيبات وعقود الصيانة لكافة معدات ومستلزمات قطاع المقاولات العامة. تأسست الشركة في الكويت عام 2018، وتعمل من خلال شراكات استراتيجية واتفاقيات توزيع معتمدة مع علامات تجارية عالمية، لتوفير منتجات عالية الجودة وأنظمة متكاملة تلبي احتياجات السوق المحلي والإقليمي. يشمل دورنا الدورة الكاملة: توريد المواد والمعدات، والتركيب والتنفيذ في الموقع، وتقديم عقود الصيانة التي تضمن استمرارية الأداء وكفاءة التشغيل. بفضل خبرتنا الفنية وكوادرنا الهندسية والإدارية، نعمل وفق أحدث الأساليب العلمية والتقنيات المتطورة، لنضمن تنفيذ المشاريع بجودة فائقة وفي زمن قياسي. نحن شريك موثوق للقطاعين العام والخاص، ونسعى دائماً لبناء علاقات طويلة الأمد قائمة على الثقة والتميز.',
    },
    location: {
      en: 'Kuwait',
      ar: 'الكويت',
    },
  },
  shared: {
    exploreServices: { en: 'Explore Services', ar: 'استكشف الخدمات' },
    contactUs: { en: 'Contact Us', ar: 'تواصل معنا' },
    learnMore: { en: 'Learn More', ar: 'اعرف المزيد' },
    since: { en: 'Since 2018 - Kuwait', ar: 'منذ 2018 - الكويت' },
    comingSoon: {
      en: 'Online submission will be enabled once backend integration is approved.',
      ar: 'سيتم تفعيل الإرسال الإلكتروني فور اعتماد الربط الخلفي.',
    },
  },
  home: {
    /** Homepage sections content */
    firstUnited: {
      contractingEthics: {
        heading: { en: 'Pro Damac Supplies — Quality and Value That Last', ar: 'توريدات برو دماك .. جودة وقيمة تدوم' },
        body: {
          en: [
            'Pro Damac is a company specialized in delivering integrated mechanical and electromechanical solutions, drawing on innovation and experience to achieve the highest standards of quality and efficiency. The company is committed to the highest levels of excellence through a complete team of engineering, technical, and administrative experts with broad expertise in electromechanical works, high- and low-voltage systems, and mechanical works in general. We rely on the latest scientific methods and advanced technologies to execute projects at exceptional quality and in record time. Guided by our forward-looking vision and distinguished workforce, we meet the demands of a fast-paced contracting sector — establishing our position as a specialized and leading company operating to the highest international standards.',
          ],
          ar: [
            'برو دماك هي شركة متخصصة في تقديم حلول الميكانيك والإلكتروميكانيك المتكاملة، معتمدة على الابتكار والخبرة لتحقيق أعلى معايير الجودة والكفاءة. تلتزم الشركة بتقديم أعلى مستويات التميز من خلال فريق متكامل من الخبراء الهندسيين والفنيين والإداريين، الذين يمتلكون خبرات واسعة في مجال الإلكتروميكانيك والجهد العالي والمنخفض وأعمال الميكانيك بشكل عام. نعتمد على أحدث الأساليب العلمية والتقنيات المتطورة لضمان تنفيذ المشاريع بجودة فائقة وفي زمن قياسي. كما نسعى، برؤيتنا المستقبلية وكوادرنا المتميزة، إلى تلبية متطلبات قطاع المقاولات المتسارع، لنرسّخ مكانتنا كشركة متخصصة ورائدة تعمل وفق أعلى المعايير العالمية.',
          ],
        },
        cta: { en: 'More about Prodamac', ar: 'المزيد عن برو دماك' },
      },
      coreValues: [
        {
          icon: 'briefcase' as const,
          title: { en: 'Integrity', ar: 'النزاهة' },
          description: { en: 'For us, this is a sign of a fair partnership', ar: 'بالنسبة لنا، هذا علامة على شراكة عادلة' },
        },
        {
          icon: 'lightbulb' as const,
          title: { en: 'Know-how', ar: 'المعرفة' },
          description: { en: 'We incorporate many years of experience into every project', ar: 'ندمج سنوات عديدة من الخبرة في كل مشروع' },
        },
        {
          icon: 'check' as const,
          title: { en: 'Quality', ar: 'الجودة' },
          description: { en: 'We realize your identity', ar: 'نحقق هويتك' },
        },
        {
          icon: 'calendarCheck' as const,
          title: { en: 'On-time delivery', ar: 'التسليم في الوقت المحدد' },
          description: { en: 'A structured implementation are essential factors', ar: 'التنفيذ المنظم عامل أساسي' },
        },
      ],
      ourServices: {
        heading: { en: 'Our Services', ar: 'خدماتنا' },
        items: [
          {
            title: { en: 'High-rise construction', ar: 'البناء العالي' },
            description: {
              en: 'The foundations of high-rise buildings support very heavy loads, but the systems developed for low-rise buildings are used, though enlarged in scale.',
              ar: 'أساسات المباني الشاهقة تدعم أحمالاً ثقيلة جداً، لكن الأنظمة المستخدمة في المباني المنخفضة تُستخدم مع تكبير النطاق.',
            },
          },
          {
            title: { en: 'General construction work', ar: 'أعمال البناء العامة' },
            description: {
              en: 'The size of buildings in the commercial, institutional, and industrial market segment ranges from a few hundred to as much as 45,000 square metres (500,000 square feet).',
              ar: 'حجم المباني في قطاع السوق التجاري والمؤسسي والصناعي يتراوح من بضع مئات إلى 45,000 متر مربع.',
            },
          },
          {
            title: { en: 'Maintenance work', ar: 'أعمال الصيانة' },
            description: {
              en: 'Maintenance is the upkeep of property, machinery, systems, and facilities, including buildings, utility infrastructure, roads, and grounds.',
              ar: 'الصيانة هي صيانة الممتلكات والآلات والأنظمة والمرافق، بما في ذلك المباني والبنية التحتية والطرق والأراضي.',
            },
          },
          {
            title: { en: 'Local & international codes', ar: 'المعايير المحلية والدولية' },
            description: {
              en: 'Prodamac is well familiar with the local and international codes such as: CSI coding system ASTM, DIN, BS Kuwait Standards Kuwait Municipality rules and regulations.',
              ar: 'برو دماك على دراية جيدة بالمعايير المحلية والدولية مثل CSI وASTM وDIN وBS ومعايير الكويت ولوائح البلدية.',
            },
          },
        ],
      },
      stats: {
        heading: { en: 'Prodamac in numbers', ar: 'برو دماك بالأرقام' },
        items: [
          { value: 10, suffix: '', label: { en: 'Year Experience', ar: 'سنوات خبرة' } },
          { value: 100, suffix: '+', label: { en: 'Finished Project', ar: 'مشروع منجز' } },
          { value: 120, suffix: '+', label: { en: 'Client', ar: 'عميل' } },
          { value: 150, suffix: '', label: { en: 'Top professionals', ar: 'محترف' } },
        ],
      },
      partners: {
        heading: { en: 'Our Partners', ar: 'شركاؤنا' },
      },
    },
    hero: {
      eyebrow: {
        en: 'Kuwait-based general trading for general contracting',
        ar: 'تجارة عامة لقطاع المقاولات العامة من الكويت',
      },
      mainHeadline: {
        en: 'General Trading for the General Contracting Sector',
        ar: 'التجارة العامة لقطاع المقاولات العامة',
      },
      title: {
        en: "Kuwait's general trading partner for the general contracting sector — built with engineering precision.",
        ar: 'شريك التجارة العامة لقطاع المقاولات العامة في الكويت — يُنفَّذ بدقة هندسية.',
      },
      rotatingServices: {
        en: ['HVAC Systems', 'Electrical Systems', 'Fire Alarm Systems', 'Elevators', 'Low Current Systems'],
        ar: ['أنظمة التكييف والتهوية', 'الأنظمة الكهربائية', 'أنظمة إنذار الحريق', 'المصاعد', 'أنظمة التيار الخفيف'],
      },
      description: {
        en: "Pro Damac is one of Kuwait's leading general trading companies, specialized in supplies, installations, and maintenance contracts for all equipment and materials of the general contracting sector.",
        ar: 'تُعدّ شركة برو دماك إحدى شركات التجارة العامة المتقدمة في الكويت، متخصصة في التوريدات والتركيبات وعقود الصيانة لكافة معدات ومستلزمات قطاع المقاولات العامة.',
      },
      supportingLine: {
        en: 'Industrial strength. Technical clarity. Reliable execution.',
        ar: 'قوة صناعية. وضوح تقني. تنفيذ موثوق.',
      },
    },
    aboutPreview: {
      eyebrow: { en: 'About Pro Damac', ar: 'عن برو دماك' },
      title: {
        en: 'A disciplined trading partner for the general contracting sector.',
        ar: 'شريك تجاري منضبط لقطاع المقاولات العامة.',
      },
      body: {
        en: "Pro Damac is one of Kuwait's leading general trading companies, specialized in supplies, installations, and maintenance contracts for all equipment and materials of the general contracting sector.",
        ar: 'تُعدّ شركة برو دماك إحدى شركات التجارة العامة المتقدمة في الكويت، متخصصة في التوريدات والتركيبات وعقود الصيانة لكافة معدات ومستلزمات قطاع المقاولات العامة.',
      },
      highlights: {
        en: ['Since 2018', 'Kuwait-based coordination', 'Supply + installation + maintenance'],
        ar: ['منذ 2018', 'تنسيق من الكويت', 'توريد + تركيب + صيانة'],
      },
    },
    /** Cinematic “About Pro Damac” block on home */
    aboutCinematic: {
      eyebrow: { en: 'Who we are', ar: 'من نحن' },
      headlineWords: {
        en: ['Engineering', 'Performance.', 'Delivering', 'Reliability.'],
        ar: [],
      },
      headlineFull: {
        en: '',
        ar: 'هندسة الأداء. توريد موثوق.',
      },
      keywordLine: {
        en: 'We build on',
        ar: 'نرتكز على',
      },
      rotatingKeywords: {
        en: ['Precision', 'Reliability', 'Performance', 'Engineering'],
        ar: ['الدقة', 'الموثوقية', 'الأداء', 'الهندسة'],
      },
      body: {
        en: "Pro Damac is one of Kuwait's leading general trading companies, specialized in supplies, installations, and maintenance contracts for all equipment and materials of the general contracting sector. Established in Kuwait in 2018, we work through strategic partnerships and authorized distribution with leading global brands to supply high-quality products and integrated systems for local and regional markets. We deliver the full cycle — supply, installation, and maintenance — with skilled teams and advanced technologies that ensure superior execution and long-term trust.",
        ar: 'تُعدّ شركة برو دماك إحدى شركات التجارة العامة المتقدمة في الكويت، متخصصة في التوريدات والتركيبات وعقود الصيانة لكافة معدات ومستلزمات قطاع المقاولات العامة. تأسست في الكويت عام 2018، وتعمل من خلال شراكات استراتيجية واتفاقيات توزيع معتمدة مع علامات عالمية، لتوفير منتجات عالية الجودة وأنظمة متكاملة تلبي احتياجات السوق المحلي والإقليمي. نغطي دورة العمل الكاملة من توريد وتركيب وصيانة، بفضل خبرتنا الفنية وأحدث التقنيات، لنضمن تنفيذاً بجودة فائقة وعلاقات طويلة الأمد قائمة على الثقة والتميز.',
      },
      features: [
        {
          icon: 'factory' as const,
          title: { en: 'Contracting sector expertise', ar: 'خبرة في قطاع المقاولات' },
          description: {
            en: 'Deep familiarity with general contracting scopes and site realities.',
            ar: 'إلمام عميق بنطاقات المقاولات العامة ومتطلبات الموقع.',
          },
        },
        {
          icon: 'globe' as const,
          title: { en: 'Global supply network', ar: 'شبكة توريد عالمية' },
          description: {
            en: 'Sourced materials and manufacturer relationships you can depend on.',
            ar: 'مواد موردة وعلاقات مصنعين يمكن الاعتماد عليها.',
          },
        },
        {
          icon: 'badge' as const,
          title: { en: 'Certified quality standards', ar: 'معايير جودة معتمدة' },
          description: {
            en: 'Disciplined checks aligned with recognized technical expectations.',
            ar: 'إجراءات تحقق منضبطة بما يتوافق مع التوقعات التقنية المعترف بها.',
          },
        },
        {
          icon: 'truck' as const,
          title: { en: 'Fast & reliable delivery', ar: 'توريد سريع وموثوق' },
          description: {
            en: 'Coordinated logistics that protect schedule and site readiness.',
            ar: 'لوجستيات منسقة تحمي البرنامج الزمني وجاهزية الموقع.',
          },
        },
      ],
      stats: {
        years: 10,
        projects: 500,
        partners: 20,
      },
      statLabels: {
        years: { en: 'Years experience', ar: 'سنوات خبرة' },
        projects: { en: 'Projects', ar: 'مشروعاً' },
        partners: { en: 'Partners', ar: 'شريكاً' },
      },
      cta: { en: 'Discover our capabilities', ar: 'اكتشف قدراتنا' },
    },
    servicesIntro: {
      eyebrow: { en: 'Core service lines', ar: 'الخطوط الخدمية الأساسية' },
      title: {
        en: 'Integrated service scopes coordinated for the general contracting sector.',
        ar: 'نطاقات خدمية متكاملة ومنسقة لقطاع المقاولات العامة.',
      },
    },
    trustedBy: {
      title: { en: 'Trusted By Leading Companies', ar: 'موثوق من شركات رائدة' },
    },
    advertisingBar: {
      badge: { en: 'Special Announcement', ar: 'إعلان خاص' },
      title: {
        en: 'Full Supply, Installation & Maintenance Packages Available for New Kuwait Projects',
        ar: 'حزم متكاملة للتوريد والتركيب والصيانة متاحة للمشاريع الجديدة في الكويت',
      },
      description: {
        en: 'From supply to installation and maintenance, our team is ready to support your next project with certified systems and reliable execution.',
        ar: 'من التوريد إلى التركيب والصيانة، فريقنا جاهز لدعم مشروعكم القادم بأنظمة معتمدة وتنفيذ موثوق.',
      },
      cta: { en: 'Request Quotation', ar: 'اطلب عرض سعر' },
    },
    deliveryIntro: {
      eyebrow: { en: 'Delivery model', ar: 'نموذج التنفيذ' },
      title: {
        en: 'One team across supply, installation, and maintenance.',
        ar: 'فريق واحد عبر التوريد والتركيب والصيانة.',
      },
      description: {
        en: 'Our operating model keeps specifications aligned, installation sequenced, and service support responsive after handover.',
        ar: 'يعتمد نموذجنا التشغيلي على توحيد المواصفات وتنظيم مراحل التركيب وضمان استجابة سريعة للدعم بعد التسليم.',
      },
    },
    whyChooseUs: {
      eyebrow: { en: 'Why choose us', ar: 'لماذا نحن' },
      title: {
        en: 'Trust is built through process, materials, and accountability.',
        ar: 'الثقة تبنى عبر الإجراءات والمواد والمسؤولية.',
      },
      description: {
        en: 'We combine technical expertise, proven partnerships, and end-to-end coordination to deliver reliable supplies, installations, and maintenance contracts for the general contracting sector.',
        ar: 'نجمع بين الخبرة التقنية والشراكات الموثوقة والتنسيق المتكامل لتقديم توريدات وتركيبات وعقود صيانة يعتمد عليها لقطاع المقاولات العامة.',
      },
      items: [
        {
          title: { en: 'Skilled technical team', ar: 'فريق تقني متخصص' },
          description: {
            en: 'Site-aware execution with attention to sequencing, system integrity, and commissioning readiness.',
            ar: 'تنفيذ واع بالموقع مع اهتمام بتسلسل الأعمال وسلامة الأنظمة وجاهزية التشغيل.',
          },
          image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80',
        },
        {
          title: { en: 'Strategic partnerships', ar: 'شراكات استراتيجية' },
          description: {
            en: 'A supplier ecosystem that strengthens material availability and technical confidence.',
            ar: 'منظومة موردين تدعم توفر المواد وتعزز الثقة الفنية في القرارات.',
          },
          image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80',
        },
        {
          title: { en: 'High-quality materials', ar: 'مواد عالية الجودة' },
          description: {
            en: 'Selections focused on durability, compliance, and operational dependability.',
            ar: 'اختيارات تركز على المتانة والامتثال والاعتمادية التشغيلية.',
          },
          image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80',
        },
        {
          title: { en: 'End-to-end coordination', ar: 'تنسيق متكامل' },
          description: {
            en: 'From procurement planning to maintenance response, responsibilities stay clearly managed.',
            ar: 'من تخطيط المشتريات إلى الاستجابة للصيانة تبقى المسؤوليات واضحة ومحددة.',
          },
          image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80',
        },
      ],
    },
    valuesList: {
      eyebrow: { en: 'Our values', ar: 'قيمنا' },
      title: { en: 'Quality – Sustainability – Excellence – Transparency – Innovation – Expertise', ar: 'الجودة – الاستدامة – التميز – الشفافية – الابتكار – الخبرة' },
      items: [
        { en: 'Quality', ar: 'الجودة' },
        { en: 'Sustainability', ar: 'الاستدامة' },
        { en: 'Excellence', ar: 'التميز' },
        { en: 'Transparency', ar: 'الشفافية' },
        { en: 'Innovation', ar: 'الابتكار' },
        { en: 'Expertise', ar: 'الخبرة' },
      ],
    },
    missionVision: {
      visionTitle: { en: 'Vision', ar: 'الرؤية' },
      visionBody: {
        en: "To be Kuwait's leading general trading company serving the general contracting sector through integrated supplies, installations, and maintenance contracts, meeting the highest international standards and positioning us as a trusted partner for both public and private sectors.",
        ar: 'أن نكون شركة التجارة العامة الرائدة في الكويت لخدمة قطاع المقاولات العامة من خلال التوريدات والتركيبات وعقود الصيانة المتكاملة، وفق أعلى المعايير العالمية، مما يعزز مكانتنا كشريك موثوق للقطاعين العام والخاص.',
      },
      missionTitle: { en: 'Mission', ar: 'الرسالة' },
      missionBody: {
        en: 'We strive to deliver innovative and flexible general trading services for the general contracting sector — supplying high-quality materials and equipment, executing installations, and providing maintenance contracts through a specialized team and strategic partnerships, ensuring quality, sustainability, and long-term relationships built on trust and excellence.',
        ar: 'نسعى لتقديم خدمات تجارة عامة مبتكرة ومرنة لقطاع المقاولات العامة، من خلال توريد المواد والمعدات عالية الجودة، وتنفيذ التركيبات، وتقديم عقود الصيانة، عبر فريق متخصص وشراكات استراتيجية، لضمان جودة التنفيذ واستدامة الأداء وبناء علاقات طويلة الأمد قائمة على الثقة والتميز.',
      },
    },
    cta: {
      title: {
        en: 'Start your project with a technically grounded partner.',
        ar: 'ابدأ مشروعك مع شريك يرتكز على فهم تقني.',
      },
      description: {
        en: 'Let us align scope, delivery priorities, and site realities before execution begins.',
        ar: 'دعنا ننسق النطاق والأولويات التنفيذية ومتطلبات الموقع قبل بدء التنفيذ.',
      },
    },
    consultationCta: {
      badge: { en: 'Request Consultation', ar: 'طلب استشارة' },
      title: {
        en: 'We Provide Expert Solutions For Your Engineering Projects',
        ar: 'نقدم حلولاً خبراء لمشاريعك الهندسية',
      },
      submitButton: { en: 'Send Request', ar: 'إرسال الطلب' },
      backgroundImage: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1920',
    },
  },
  about: {
    pageWelcome: {
      en: 'Welcome to the Pro Damac General Trading company website.',
      ar: 'مرحباً بكم في موقع شركة برو دماك للتجارة العامة.',
    },
    heroTitle: {
      en: "One of Kuwait's leading general trading companies, serving the general contracting sector.",
      ar: 'إحدى شركات التجارة العامة المتقدمة في الكويت، في خدمة قطاع المقاولات العامة.',
    },
    heroDescription: {
      en: "Pro Damac is specialized in supplies, installations, and maintenance contracts for all equipment and materials of the general contracting sector — delivered with clarity, control, and practical coordination across procurement, site work, and post-installation support.",
      ar: 'تتخصص برو دماك في التوريدات والتركيبات وعقود الصيانة لكافة معدات ومستلزمات قطاع المقاولات العامة، مع تنفيذ يتميز بالوضوح والتحكم والتنسيق العملي عبر المشتريات وأعمال الموقع ودعم ما بعد التركيب.',
    },
    valuesIntro: {
      eyebrow: { en: 'Company values', ar: 'قيم الشركة' },
      title: {
        en: 'The operating principles behind every scope.',
        ar: 'المبادئ التشغيلية خلف كل نطاق عمل.',
      },
      description: {
        en: 'Our internal standards are designed to protect quality, schedule confidence, and client visibility.',
        ar: 'تم تصميم معاييرنا الداخلية لحماية الجودة ودعم الثقة في البرنامج الزمني وتعزيز وضوح الصورة أمام العميل.',
      },
    },
    values: [
      {
        title: { en: 'Quality', ar: 'الجودة' },
        description: {
          en: 'We commit to the highest standards in materials, execution, and deliverables.',
          ar: 'الالتزام بأعلى المعايير في المواد والتنفيذ والمخرجات.',
        },
      },
      {
        title: { en: 'Innovation', ar: 'الابتكار' },
        description: {
          en: 'We adopt the latest methods and technologies to deliver better outcomes.',
          ar: 'اعتماد أحدث الأساليب والتقنيات لتحقيق نتائج أفضل.',
        },
      },
      {
        title: { en: 'Transparency', ar: 'الشفافية' },
        description: {
          en: 'Clear communication and visibility at every stage of the project.',
          ar: 'وضوح التواصل والرؤية في كل مرحلة من مراحل المشروع.',
        },
      },
      {
        title: { en: 'Excellence', ar: 'التميز' },
        description: {
          en: 'We strive for superior performance and lasting client relationships.',
          ar: 'السعي لأداء متميز وعلاقات عمل دائمة قائمة على الثقة.',
        },
      },
      {
        title: { en: 'Sustainability', ar: 'الاستدامة' },
        description: {
          en: 'Systems and practices that ensure long-term performance and efficiency.',
          ar: 'أنظمة وممارسات تضمن الأداء والكفاءة على المدى الطويل.',
        },
      },
      {
        title: { en: 'Expertise', ar: 'الخبرة' },
        description: {
          en: 'Skilled engineers and technicians backed by strategic partnerships.',
          ar: 'فريق هندسي وفني متخصص مدعوم بشراكات استراتيجية.',
        },
      },
    ],
    timeline: [
      {
        year: '2018',
        title: { en: 'Company established in Kuwait', ar: 'تأسيس الشركة في الكويت' },
        description: {
          en: 'Pro Damac began operations with a focus on coordinated general trading for the general contracting sector.',
          ar: 'بدأت برو دماك عملياتها مع تركيز على التجارة العامة المنسقة لقطاع المقاولات العامة.',
        },
      },
      {
        year: '2020',
        title: { en: 'Partnership network expanded', ar: 'توسيع شبكة الشراكات' },
        description: {
          en: 'Supplier and manufacturer relationships were strengthened to support wider project coverage.',
          ar: 'تم تعزيز العلاقات مع الموردين والمصنعين لدعم تغطية أوسع للمشاريع.',
        },
      },
      {
        year: '2022',
        title: { en: 'Integrated delivery model sharpened', ar: 'تعزيز نموذج التنفيذ المتكامل' },
        description: {
          en: 'Supply, installation, and maintenance were formalized as one coordinated operating flow.',
          ar: 'تم ترسيخ التوريد والتركيب والصيانة كنموذج تشغيلي متكامل ومنسق.',
        },
      },
      {
        year: '2024',
        title: { en: 'Systems capability broadened', ar: 'توسيع قدرات الأنظمة' },
        description: {
          en: 'Low current, security, and fire alarm coordination became a stronger part of the service mix.',
          ar: 'أصبحت أنظمة التيار الخفيف والأمن وإنذار الحريق جزءاً أقوى من منظومة الخدمات.',
        },
      },
    ],
  },
  projectsPage: {
    bannerImage: '/images/remove_shadow_2K_202603301518.png',
    introTitle: {
      en: 'Empowering partnerships & building success together',
      ar: 'شراكات تمكّن ونجاح نبنيه معاً',
    },
    introBody: {
      en: 'From landmark towers to critical infrastructure, we coordinate supplies, installations, and maintenance scopes for the general contracting sector with suppliers, site teams, and long-term operational needs—so clients can focus on delivery with confidence.',
      ar: 'من الأبراج البارزة إلى البنية التحتية الحيوية، ننسّق التوريدات والتركيبات ونطاقات الصيانة لقطاع المقاولات العامة مع الموردين وفرق الموقع ومتطلبات التشغيل طويل الأمد—ليتمكن عملاؤنا من التسليم بثقة.',
    },
    categories: [
      {
        id: 'high-rise',
        title: { en: 'High-rise projects', ar: 'مشاريع ناطحات سحاب' },
        subtitle: {
          en: 'Our pride in powering client achievements',
          ar: 'نفتخر بدعم إنجازات عملائنا',
        },
        items: [
          {
            id: 'hr-1',
            imageSrc: 'https://images.unsplash.com/photo-1486322303233-82814245db0e?w=1200&q=80',
            title: { en: 'Corporate headquarters — MEP coordination', ar: 'مقر إداري — تنسيق كهروميكانيكي' },
          },
          {
            id: 'hr-2',
            imageSrc: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=80',
            title: { en: 'Mixed-use tower — HVAC & electrical', ar: 'برج متعدد الاستخدام — تكييف وكهرباء' },
          },
          {
            id: 'hr-3',
            imageSrc: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1200&q=80',
            title: { en: 'Urban landmark — systems integration', ar: 'معلم حضري — تكامل أنظمة' },
          },
          {
            id: 'hr-4',
            imageSrc: 'https://images.unsplash.com/photo-1460472178825-e5240623afd5?w=1200&q=80',
            title: { en: 'Waterfront development — life-safety & LV', ar: 'تطوير على الواجهة البحرية — سلامة وتيار خفيف' },
          },
          {
            id: 'hr-5',
            imageSrc: 'https://images.unsplash.com/photo-1511818966892-d7d671e672a2?w=1200&q=80',
            title: { en: 'High-rise residential — plumbing & fire alarm', ar: 'سكني عالٍ — صحي وإنذار حريق' },
          },
          {
            id: 'hr-6',
            imageSrc: 'https://images.unsplash.com/photo-1494145904049-0dca59b4bbad?w=1200&q=80',
            title: { en: 'CBD expansion — elevator & low-current', ar: 'توسعة مركز الأعمال — مصاعد وتيار ضعيف' },
          },
        ],
      },
      {
        id: 'commercial',
        title: { en: 'Commercial & institutional', ar: 'تجاري ومؤسسي' },
        subtitle: {
          en: 'Precision delivery across offices, retail, and public facilities',
          ar: 'تنفيذ دقيق للمكاتب والتجزئة والمرافق العامة',
        },
        items: [
          {
            id: 'co-1',
            imageSrc: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80',
            title: { en: 'Office campus — fit-out systems', ar: 'مجمع مكاتب — أنظمة التشطيبات' },
          },
          {
            id: 'co-2',
            imageSrc: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&q=80',
            title: { en: 'Retail complex — mechanical & electrical', ar: 'مجمع تجاري — ميكانيكية وكهرباء' },
          },
          {
            id: 'co-3',
            imageSrc: 'https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=1200&q=80',
            title: { en: 'Public facility — security & access', ar: 'منشأة عامة — أمن وتحكم بالدخول' },
          },
        ],
      },
    ] satisfies ProjectCategoryBlock[],
  },
  servicesPage: {
    heroTitle: {
      en: 'Supply, installation, and maintenance scopes structured for dependable execution in the general contracting sector.',
      ar: 'نطاقات التوريد والتركيب والصيانة مصممة لتنفيذ موثوق في قطاع المقاولات العامة.',
    },
    heroDescription: {
      en: 'Each service line is organized around technical clarity, site readiness, and lifecycle support.',
      ar: 'كل خط خدمة منظم حول الوضوح الفني وجاهزية الموقع والدعم خلال دورة حياة النظام.',
    },
  },
  resourcesPage: {
    heroTitle: {
      en: 'Technical resources, references, and downloadable materials.',
      ar: 'موارد تقنية ومراجع ومواد قابلة للتنزيل.',
    },
    heroDescription: {
      en: 'Browse guides and documentation published by the team. Content is managed from the dashboard.',
      ar: 'تصفح الأدلة والوثائق التي ينشرها الفريق. يُدار المحتوى من لوحة التحكم.',
    },
  },
  solutionsPage: {
    heroTitle: {
      en: 'Capability-led solutions for industrial, infrastructure, and commercial projects.',
      ar: 'حلول قائمة على القدرات للمشاريع الصناعية والبنية التحتية والقطاعات التجارية.',
    },
    heroDescription: {
      en: 'We present solution categories and delivery methods, not fictional project showcases.',
      ar: 'نركز على فئات الحلول وأساليب التنفيذ وليس على عرض مشاريع افتراضية.',
    },
    processTitle: { en: 'How we structure delivery', ar: 'كيف ننظم التنفيذ' },
    processDescription: {
      en: 'A repeatable process that keeps specifications, sequencing, and service continuity aligned.',
      ar: 'عملية قابلة للتكرار تحافظ على توافق المواصفات وتسلسل الأعمال واستمرارية الخدمة.',
    },
    processSteps: [
      {
        title: { en: 'Scope review', ar: 'مراجعة النطاق' },
        description: {
          en: 'Clarify requirements, interfaces, and procurement expectations early.',
          ar: 'توضيح المتطلبات ونقاط الربط وتوقعات المشتريات مبكراً.',
        },
      },
      {
        title: { en: 'System coordination', ar: 'تنسيق الأنظمة' },
        description: {
          en: 'Align equipment, installation sequencing, and on-site constraints.',
          ar: 'مواءمة المعدات وتسلسل التركيب وقيود الموقع.',
        },
      },
      {
        title: { en: 'Operational handover', ar: 'التسليم التشغيلي' },
        description: {
          en: 'Support testing, readiness, and post-installation response planning.',
          ar: 'دعم الاختبارات والجاهزية وخطط الاستجابة بعد التركيب.',
        },
      },
    ],
  },
  contactPage: {
    pageWelcome: {
      en: 'We are here to help with project coordination, technical questions, and partnership inquiries.',
      ar: 'نحن هنا لمساعدتكم في تنسيق المشاريع والاستفسارات الفنية وطلبات الشراكة.',
    },
    heroTitle: {
      en: 'Start the conversation with scope, timing, and system needs.',
      ar: 'ابدأ الحوار حول النطاق والجدول واحتياجات الأنظمة.',
    },
    heroDescription: {
      en: 'Use the contact page to organize your inquiry. Live submission remains disabled until backend approval is completed.',
      ar: 'استخدم صفحة التواصل لتنظيم الاستفسار. الإرسال المباشر ما زال معطلاً حتى اكتمال اعتماد الربط الخلفي.',
    },
    formTitle: { en: 'Project inquiry form', ar: 'نموذج استفسار المشروع' },
    formDescription: {
      en: 'Share the project type, required disciplines, and timing. The form validates locally and stores nothing server-side.',
      ar: 'شارك نوع المشروع والتخصصات المطلوبة والإطار الزمني. النموذج يتحقق محلياً ولا يخزن أي بيانات على الخادم.',
    },
    mapTitle: { en: 'Service area', ar: 'نطاق الخدمة' },
    mapDescription: {
      en: 'Kuwait-based operations with coordination for industrial, infrastructure, and commercial environments.',
      ar: 'عملياتنا تنطلق من الكويت مع تنسيق للمشاريع الصناعية والبنية التحتية والقطاعات التجارية.',
    },
    infoCards: [
      {
        label: { en: 'Business email', ar: 'البريد الإلكتروني' },
        value: { en: 'info@prodamac.com', ar: 'info@prodamac.com' },
        note: {
          en: 'Placeholder until approved contact address is shared.',
          ar: 'قيمة مؤقتة حتى اعتماد عنوان التواصل النهائي.',
        },
      },
      {
        label: { en: 'Phone', ar: 'الهاتف' },
        value: { en: '+965 XXX XXXX', ar: '+965 XXX XXXX' },
        note: {
          en: 'Approved direct line to be added.',
          ar: 'سيتم إضافة الرقم المباشر المعتمد.',
        },
      },
      {
        label: { en: 'Office location', ar: 'موقع المكتب' },
        value: { en: 'Kuwait City, Kuwait', ar: 'مدينة الكويت، الكويت' },
        note: {
          en: 'Full address placeholder pending final confirmation.',
          ar: 'العنوان الكامل مؤقت لحين التأكيد النهائي.',
        },
      },
    ],
  },
  footer: {
    shortAbout: {
      en: "Kuwait-based since 2018. General trading for Kuwait's general contracting sector — supply, install, maintain — with trusted partners and proven quality.",
      ar: 'شركة منذ 2018. التجارة العامة لقطاع المقاولات العامة في الكويت — توريد، تركيب، صيانة — مع شركاء موثوقين وجودة مضمونة.',
    },
    address: {
      en: 'Capital - Shuwaikh Industrial (1) Block (1) – Plot (142) - Sun City Complex - Damac Gulf Building – Block A',
      ar: 'العاصمة - الشويخ الصناعية (1) قطعة (1) – قسيمة (142) - مجمع صن سيتي - مبنى دماك الخليجية – بلوك A',
    },
    phones: [
      { en: '+965 2225 0588', ar: '22250588' },
      { en: '+965 6630 3766', ar: '66303766' },
      { en: '+965 9442 9253', ar: '94429253' },
    ],
    emails: [
      { value: 'info@prodamac.com', label: { en: 'Info', ar: 'الاستفسارات' } },
      { value: 'sales@prodamac.com', label: { en: 'Sales', ar: 'المبيعات' } },
    ],
    social: [
      { id: 'instagram', url: 'https://www.instagram.com/prodamac.kw', label: { en: 'Instagram', ar: 'انستجرام' } },
      { id: 'tiktok', url: 'https://www.tiktok.com/@prodamac.kw', label: { en: 'TikTok', ar: 'تيك توك' } },
      { id: 'facebook', url: 'https://www.facebook.com/people/Prodamackw/61581150488873/', label: { en: 'Facebook', ar: 'فيس بوك' } },
      { id: 'linkedin', url: 'https://www.linkedin.com/in/prodamac-kw-9768b2385/', label: { en: 'LinkedIn', ar: 'لينكد إن' } },
      { id: 'linktree', url: 'https://linktr.ee/Prodamackw', label: { en: 'Linktree', ar: 'لينك تري' } },
    ],
    copyright: {
      en: '© {year} Pro Damac General Trading. All rights reserved.',
      ar: '© {year} برو دماك للتجارة العامة. جميع الحقوق محفوظة.',
    },
  },
  errors: {
    notFound: {
      title: { en: 'The page you requested could not be found.', ar: 'تعذر العثور على الصفحة المطلوبة.' },
      description: {
        en: 'The route may have changed, or the page may not exist in the current language path.',
        ar: 'قد يكون المسار قد تغير أو أن الصفحة غير متوفرة ضمن مسار اللغة الحالي.',
      },
    },
    server: {
      title: { en: 'A server-side issue interrupted the request.', ar: 'حدثت مشكلة من جهة الخادم وأوقفت الطلب.' },
      description: {
        en: 'Please try again later or return to the main site sections while backend services are restored.',
        ar: 'يرجى المحاولة لاحقاً أو العودة إلى أقسام الموقع الرئيسية حتى تتم استعادة الخدمات الخلفية.',
      },
    },
    network: {
      title: { en: 'Network connectivity is currently unavailable.', ar: 'الاتصال بالشبكة غير متاح حالياً.' },
      description: {
        en: 'Check your connection and retry when access is restored.',
        ar: 'تحقق من الاتصال ثم أعد المحاولة عند استقرار الوصول.',
      },
    },
  },
}

export const stats: StatItem[] = [
  { id: 'years', value: yearsInOperation, suffix: '+', label: { en: 'Years in operation', ar: 'سنوات من التشغيل' } },
  { id: 'services', value: 12, label: { en: 'Core service lines', ar: 'خطوط خدمية أساسية' } },
  { id: 'delivery', value: 3, label: { en: 'Delivery modes', ar: 'أنماط تنفيذ' } },
]

export const serviceItems: ServiceItem[] = [
  {
    id: 'hvac',
    icon: 'hvac',
    title: { en: 'HVAC Systems', ar: 'أنظمة التكييف والتهوية' },
    description: {
      en: 'Cooling, ventilation, and comfort systems designed for stable performance and maintainable operation.',
      ar: 'أنظمة التبريد والتهوية والراحة المصممة لأداء مستقر وتشغيل سهل الصيانة.',
    },
    bullets: {
      en: ['Equipment supply coordination', 'Duct and airflow planning', 'Installation and service readiness'],
      ar: ['تنسيق توريد المعدات', 'تخطيط مجاري الهواء وتوزيع التدفق', 'التركيب والجاهزية للخدمة'],
    },
  },
  {
    id: 'plumbing',
    icon: 'plumbing',
    title: { en: 'Plumbing', ar: 'السباكة' },
    description: {
      en: 'Water distribution and sanitary scope delivered with durability, access, and practical maintenance in mind.',
      ar: 'تنفيذ أعمال المياه والصرف مع التركيز على المتانة وسهولة الوصول وإمكانية الصيانة العملية.',
    },
    bullets: {
      en: ['Water and drainage coordination', 'Material suitability review', 'Installation sequencing support'],
      ar: ['تنسيق شبكات المياه والصرف', 'مراجعة ملاءمة المواد', 'دعم تسلسل التنفيذ'],
    },
  },
  {
    id: 'electrical',
    icon: 'electrical',
    title: { en: 'Electrical Systems', ar: 'الأنظمة الكهربائية' },
    description: {
      en: 'Power, distribution, and essential electrical scopes aligned with safety and operational continuity.',
      ar: 'نطاقات القدرة والتوزيع والأعمال الكهربائية الأساسية مع مراعاة السلامة واستمرارية التشغيل.',
    },
    bullets: {
      en: ['Distribution and cable coordination', 'Site-ready installation planning', 'Commissioning support paths'],
      ar: ['تنسيق التوزيع والكابلات', 'تخطيط التركيب الجاهز للموقع', 'مسارات دعم التشغيل'],
    },
  },
  {
    id: 'elevators',
    icon: 'elevators',
    title: { en: 'Elevators', ar: 'المصاعد' },
    description: {
      en: 'Vertical transportation scopes coordinated for integration, access, and reliable user experience.',
      ar: 'تنسيق أعمال النقل الرأسي لتحقيق تكامل الأنظمة وسهولة الوصول وموثوقية الاستخدام.',
    },
    bullets: {
      en: ['System and shaft interface coordination', 'Installation supervision readiness', 'Maintenance planning support'],
      ar: ['تنسيق الواجهات بين النظام والبئر', 'جاهزية الإشراف على التركيب', 'دعم تخطيط الصيانة'],
    },
  },
  {
    id: 'low-current',
    icon: 'lowCurrent',
    title: { en: 'Low Current Systems', ar: 'أنظمة التيار الخفيف' },
    description: {
      en: 'Communication and control systems structured for connectivity, integration, and future serviceability.',
      ar: 'أنظمة الاتصال والتحكم المصممة للربط والتكامل وسهولة الخدمة المستقبلية.',
    },
    bullets: {
      en: ['Structured cabling pathways', 'Control and communications coordination', 'Expandable infrastructure planning'],
      ar: ['مسارات الكوابل المنظمة', 'تنسيق أنظمة التحكم والاتصالات', 'تخطيط بنية قابلة للتوسع'],
    },
  },
  {
    id: 'security',
    icon: 'security',
    title: { en: 'Security Systems', ar: 'أنظمة الأمن' },
    description: {
      en: 'Security and surveillance scopes planned to strengthen monitoring, coverage, and operational confidence.',
      ar: 'أعمال الأمن والمراقبة المخططة لتعزيز التغطية والرصد والثقة التشغيلية.',
    },
    bullets: {
      en: ['Access and monitoring coordination', 'Coverage-driven device planning', 'Maintenance response preparedness'],
      ar: ['تنسيق أنظمة الوصول والمراقبة', 'تخطيط الأجهزة وفق التغطية المطلوبة', 'الجاهزية لاستجابة الصيانة'],
    },
  },
  {
    id: 'fire-alarm',
    icon: 'fireAlarm',
    title: { en: 'Fire Alarm Systems', ar: 'أنظمة إنذار الحريق' },
    description: {
      en: 'Life-safety signaling scopes managed with clarity, coordination, and readiness for reliable response.',
      ar: 'إدارة نطاقات الإنذار والسلامة بوضوح وتنسيق وجاهزية لاستجابة موثوقة.',
    },
    bullets: {
      en: ['Detection and alarm interface review', 'Code-aware installation support', 'Testing and handover readiness'],
      ar: ['مراجعة واجهات الكشف والإنذار', 'دعم التنفيذ وفق متطلبات الكود', 'الجاهزية للاختبار والتسليم'],
    },
  },
]

export const deliveryModes = [
  {
    title: { en: 'Supply', ar: 'التوريد' },
    description: {
      en: 'Material and equipment sourcing aligned with technical requirements, availability, and project timing.',
      ar: 'تأمين المواد والمعدات بما يتوافق مع المتطلبات الفنية والتوفر والبرنامج الزمني.',
    },
  },
  {
    title: { en: 'Installation', ar: 'التركيب' },
    description: {
      en: 'Disciplined site execution with attention to sequencing, coordination, and build quality.',
      ar: 'تنفيذ موقعي منضبط يراعي تسلسل الأعمال والتنسيق وجودة التركيب.',
    },
  },
  {
    title: { en: 'Maintenance', ar: 'الصيانة' },
    description: {
      en: 'Post-handover support focused on responsiveness, system continuity, and practical uptime.',
      ar: 'دعم ما بعد التسليم مع التركيز على سرعة الاستجابة واستمرارية الأنظمة والجاهزية التشغيلية.',
    },
  },
]

export const solutionCategories: SolutionCategory[] = [
  {
    id: 'industrial',
    icon: 'industrial',
    title: { en: 'Industrial', ar: 'صناعي' },
    description: {
      en: 'Solutions designed for facilities where continuity, coordination, and durable system performance are critical.',
      ar: 'حلول موجهة للمرافق التي تعتمد على الاستمرارية والتنسيق والأداء المتين للأنظمة.',
    },
    bullets: {
      en: ['Plants and production spaces', 'Warehouses and logistics facilities', 'Utility and support infrastructure'],
      ar: ['المصانع ومساحات الإنتاج', 'المستودعات ومرافق الخدمات اللوجستية', 'البنية المساندة والخدمية'],
    },
  },
  {
    id: 'infrastructure',
    icon: 'infrastructure',
    title: { en: 'Infrastructure', ar: 'بنية تحتية' },
    description: {
      en: 'System packages aligned with public assets, support facilities, and coordinated service environments.',
      ar: 'حزم أنظمة تتوافق مع الأصول العامة والمرافق المساندة وبيئات الخدمات المنسقة.',
    },
    bullets: {
      en: ['Public support facilities', 'Utility-linked buildings', 'Multi-discipline coordination requirements'],
      ar: ['المرافق الداعمة العامة', 'المباني المرتبطة بالخدمات', 'متطلبات التنسيق متعدد التخصصات'],
    },
  },
  {
    id: 'commercial',
    icon: 'commercial',
    title: { en: 'Commercial', ar: 'تجاري' },
    description: {
      en: 'Execution for offices, mixed-use assets, retail spaces, and commercial environments that demand polished coordination.',
      ar: 'تنفيذ للمكاتب والمشاريع متعددة الاستخدامات ومساحات التجزئة والبيئات التجارية التي تتطلب تنسيقاً عالي الجودة.',
    },
    bullets: {
      en: ['Office and mixed-use developments', 'Retail and customer-facing spaces', 'Hospitality and administrative environments'],
      ar: ['تطويرات المكاتب ومتعددة الاستخدامات', 'مساحات التجزئة والواجهات الخدمية', 'الضيافة والبيئات الإدارية'],
    },
  },
]

export const contactFormInitialValues: ContactFormValues = {
  name: '',
  email: '',
  company: '',
  phone: '',
  address: '',
  message: '',
}

export const formFieldLabels: Record<keyof ContactFormValues, LocalizedCopy<string>> = {
  name: { en: 'Full name', ar: 'الاسم الكامل' },
  email: { en: 'Business email', ar: 'البريد الإلكتروني' },
  company: { en: 'Company', ar: 'الشركة' },
  phone: { en: 'Phone', ar: 'الهاتف' },
  address: { en: 'Address', ar: 'العنوان' },
  message: { en: 'Project details', ar: 'تفاصيل المشروع' },
}

export const formPlaceholders: Record<keyof ContactFormValues, LocalizedCopy<string>> = {
  name: { en: 'Enter your name', ar: 'أدخل اسمك' },
  email: { en: 'Enter your business email', ar: 'أدخل بريدك الإلكتروني' },
  company: { en: 'Enter company name', ar: 'أدخل اسم الشركة' },
  phone: { en: 'Enter phone number', ar: 'أدخل رقم الهاتف' },
  address: { en: 'Enter your address', ar: 'أدخل عنوانك' },
  message: {
    en: 'Outline the project scope, discipline, and timing',
    ar: 'وضح نطاق المشروع والتخصصات والإطار الزمني',
  },
}
